import { useEffect, useRef, useState } from "react";

import Dialog from "@mui/material/Dialog";
import LinearProgress from "@mui/material/LinearProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import ReferralTerms from "layouts/agreements/referral/ReferralTerms";
import AuthService from "services/auth.service";
import { useMaterialUIController } from "context";
import { useSelector } from "react-redux";
import {
  forgetTermsAccepted,
  hasAcceptedTerms,
  rememberTermsAccepted,
  skipTermsForSession,
  wasTermsAcceptedRecently,
  wasTermsSkipped,
} from "utils/termsAcceptance";
import Cookies from "js-cookie";

const SCROLL_END_GAP = 8;

function TermsAcceptanceModal() {
  const [controller] = useMaterialUIController();
  const { layout } = controller;
  const agent = useSelector((state) => state.data.agent);
  const isDataLoaded = useSelector((state) => state.data.isDataLoaded);

  const scrollRef = useRef(null);
  const [skipped, setSkipped] = useState(wasTermsSkipped);
  const [percent, setPercent] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const sessionId = Cookies.get("referral_id");
  const username = Cookies.get("username");
  const apiAccepted = hasAcceptedTerms(
    agent?.accepted_t_c ?? agent?.accepted_t_c__c ?? agent?.Accepted_T_C__c
  );
  const accepted = apiAccepted || wasTermsAcceptedRecently(username);
  const open = isDataLoaded && layout === "dashboard" && !accepted && !skipped;

  useEffect(() => {
    setSkipped(wasTermsSkipped());
  }, [sessionId]);

  useEffect(() => {
    if (isDataLoaded && apiAccepted) forgetTermsAccepted(username);
  }, [isDataLoaded, apiAccepted, username]);
  const nameIsValid = legalName.trim().split(/\s+/).filter(Boolean).length >= 2;
  const complete = percent >= 100;
  const canAccept = complete && agreed && nameIsValid && !submitting;

  const updateProgress = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= SCROLL_END_GAP) {
      setPercent(100);
      return;
    }
    const atEnd = el.scrollTop >= max - SCROLL_END_GAP;
    // Cap the label at 99 until the end. Rounding could show 100% while the
    // checkbox was still disabled. Once they reach the end, keep 100%.
    const next = atEnd ? 100 : Math.min(99, Math.round((el.scrollTop / max) * 100));
    setPercent((current) => (current >= 100 ? 100 : next));
  };

  useEffect(() => {
    if (!open) return undefined;
    setPercent(0);
    setAgreed(false);
    setLegalName("");
    setError("");
    const frame = requestAnimationFrame(updateProgress);
    const timer = setTimeout(updateProgress, 100);
    window.addEventListener("resize", updateProgress);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      window.removeEventListener("resize", updateProgress);
    };
  }, [open]);

  const jumpToEnd = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const handleSkip = () => {
    skipTermsForSession();
    setSkipped(true);
  };

  const handleAccept = () => {
    if (!canAccept) return;
    setSubmitting(true);
    setError("");
    AuthService.acceptTerms()
      .then(() => {
        rememberTermsAccepted(username);
        setSubmitting(false);
      })
      .catch(() => {
        setSubmitting(false);
        setError("We couldn't save your acceptance. Please try again.");
      });
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      sx={{ zIndex: 20000 }}
    >
      <MDBox p={3}>
        <MDTypography variant="h5" color="soliddark">
          Our Terms &amp; Conditions have been updated.
        </MDTypography>
        <MDTypography variant="body2" color="text" mt={1}>
          Please review and accept the updated terms to continue.
        </MDTypography>

        <MDBox display="flex" alignItems="center" gap={2} mt={2} mb={1}>
          <MDBox flexGrow={1}>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                "& .MuiLinearProgress-bar": {
                  width: `${percent}%`,
                  transform: "none !important",
                  backgroundColor: complete ? "#4DA917" : "#e91e63",
                },
              }}
            />
          </MDBox>
          <MDTypography variant="button" color={complete ? "solidsuccess" : "text"} sx={{ minWidth: 72 }}>
            {percent}% read
          </MDTypography>
          <MDButton variant="outlined" color="info" size="small" onClick={jumpToEnd}>
            Jump to end
          </MDButton>
        </MDBox>

        <MDBox
          ref={scrollRef}
          onScroll={updateProgress}
          sx={{
            maxHeight: "45vh",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            px: 2,
            py: 1,
          }}
        >
          <MDTypography align="center" variant="h6" color="soliddark" mb={1}>
            MyAgentFinder Referral Network
          </MDTypography>
          <MDTypography align="center" variant="h6" color="soliddark" mb={2}>
            Agent Terms and Conditions
          </MDTypography>
          <ReferralTerms />
        </MDBox>

        <MDBox mt={2} sx={{ opacity: complete ? 1 : 0.55 }}>
          {!complete && (
            <MDTypography variant="caption" color="text">
              Scroll to the end of the terms to accept.
            </MDTypography>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
            <input
              id="terms-read"
              type="checkbox"
              checked={agreed}
              disabled={!complete || submitting}
              onChange={(event) => setAgreed(event.target.checked)}
              style={{
                width: 18,
                height: 18,
                margin: 0,
                accentColor: "#4DA917",
                cursor: complete && !submitting ? "pointer" : "default",
              }}
            />
            <MDTypography
              component="label"
              htmlFor="terms-read"
              variant="button"
              color="dark"
              sx={{ cursor: complete && !submitting ? "pointer" : "default", userSelect: "none" }}
            >
              I have read and agree to the Terms &amp; Conditions
            </MDTypography>
          </MDBox>
          <MDInput
            fullWidth
            label="Full legal name"
            value={legalName}
            disabled={!complete || submitting}
            onChange={(event) => setLegalName(event.target.value)}
            sx={{ mt: 2 }}
          />
          {complete && legalName.trim().length > 0 && !nameIsValid && (
            <MDTypography variant="caption" color="error">
              Enter your first and last name.
            </MDTypography>
          )}
        </MDBox>

        {error && (
          <MDTypography variant="caption" color="error" display="block" mt={1}>
            {error}
          </MDTypography>
        )}

        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <MDButton variant="text" color="dark" onClick={handleSkip} disabled={submitting}>
            Skip for now
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleAccept} disabled={!canAccept}>
            {submitting ? "Saving..." : "Accept"}
          </MDButton>
        </MDBox>
      </MDBox>
    </Dialog>
  );
}

export default TermsAcceptanceModal;
